import React, { useState } from 'react';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../lib/supabaseClient';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  CreditCard,
  Crown,
  Gem,
  Sparkles,
  CalendarCheck,
  Settings2,
  Loader2,
  Info,
  Check,
  Clock,
  Star,
} from 'lucide-react';

const PLAN_INFO = {
  gratuito: {
    label: 'Gratuito',
    color: 'text-slate-700',
    bg: 'bg-slate-100',
    border: 'border-slate-300',
    Icon: Sparkles,
    description: 'Você está usando o plano gratuito. Faça upgrade para desbloquear mais recursos.',
  },
  platinum: {
    label: 'Platinum',
    color: 'text-[#047857]',
    bg: 'bg-emerald-50',
    border: 'border-emerald-400',
    Icon: Crown,
    description: 'Obrigado por ser Platinum! Você tem acesso a todos os recursos essenciais.',
  },
  diamond: {
    label: 'Diamond',
    color: 'text-[#1B2A4A]',
    bg: 'bg-indigo-50',
    border: 'border-indigo-400',
    Icon: Gem,
    description: 'Você é Diamond — o plano mais completo. Obrigado pelo apoio!',
  },
};

const STATUS_LABELS = {
  active: { label: 'Ativa', color: 'text-green-700', bg: 'bg-green-100' },
  trialing: { label: 'Em teste', color: 'text-blue-700', bg: 'bg-blue-100' },
  past_due: { label: 'Pagamento pendente', color: 'text-amber-700', bg: 'bg-amber-100' },
  canceled: { label: 'Cancelada', color: 'text-red-700', bg: 'bg-red-100' },
  incomplete: { label: 'Incompleta', color: 'text-amber-700', bg: 'bg-amber-100' },
  unpaid: { label: 'Não paga', color: 'text-red-700', bg: 'bg-red-100' },
};

const PRICES = {
  platinum: {
    monthly: { amount: 'R$ 14,90', suffix: '/mês' },
    annual: { amount: 'R$ 149,00', suffix: '/ano' },
  },
  diamond: {
    monthly: { amount: 'R$ 29,90', suffix: '/mês' },
    annual: { amount: 'R$ 299,00', suffix: '/ano' },
  },
};

const PLATINUM_FEATURES = [
  { label: 'Tudo do plano Gratuito', soon: false },
  { label: 'Rendas ilimitadas', soon: false },
  { label: 'Metas ilimitadas', soon: false },
  { label: 'Histórico completo', soon: false },
  { label: 'Relatório anual', soon: false },
  { label: 'Categorias personalizadas', soon: false },
  { label: 'Alertas inteligentes', soon: false },
];

const DIAMOND_FEATURES = [
  { label: 'Tudo do Platinum', soon: false },
  { label: 'Exportar relatório em PDF', soon: false },
  { label: 'Projeção de 12 meses', soon: false },
  { label: 'Exportar e importar dados', soon: false },
  { label: 'Upload de extrato bancário', soon: true },
  { label: 'Suporte prioritário', soon: false },
];

function formatBRDate(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    });
  } catch {
    return null;
  }
}

function FeatureList({ features }) {
  return (
    <ul className="space-y-2.5 mb-6 flex-1">
      {features.map((f) => (
        <li
          key={f.label}
          className={`flex items-start gap-2 text-base ${f.soon ? 'text-slate-400' : 'text-slate-700'}`}
        >
          <Check size={18} className={`${f.soon ? 'text-slate-300' : 'text-green-600'} shrink-0 mt-0.5`} />
          <span className="flex items-center gap-2 flex-wrap">
            {f.label}
            {f.soon && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 text-xs font-semibold">
                <Clock size={12} />
                Em breve
              </span>
            )}
          </span>
        </li>
      ))}
    </ul>
  );
}

function MinhaContaPage() {
  const { user } = useAuth();
  const [opening, setOpening] = useState(false);
  const [checkingOut, setCheckingOut] = useState(null); // 'platinum' | 'diamond' | null
  const [billing, setBilling] = useState('monthly');

  const plan = user?.plan || 'gratuito';
  const status = user?.subscription_status || null;
  const periodEnd = user?.current_period_end || null;
  const info = PLAN_INFO[plan] || PLAN_INFO.gratuito;
  const PlanIcon = info.Icon;

  const isPaidPlan = plan === 'platinum' || plan === 'diamond';
  const hasStripeCustomer = Boolean(user?.stripe_customer_id);
  const statusMeta = status ? STATUS_LABELS[status] : null;

  const openPortal = async () => {
    setOpening(true);
    try {
      const { data, error } = await supabase.functions.invoke(
        'create-portal-session',
      );
      if (error) throw error;
      if (data?.url) {
        window.location.href = data.url;
      } else {
        throw new Error('Portal URL not returned');
      }
    } catch (err) {
      console.error('Portal session failed:', err);
      toast.error('Não foi possível abrir o gerenciamento da assinatura. Tente novamente.');
      setOpening(false);
    }
  };

  // Gratuito → straight to Stripe Checkout.
  // Paid plan → send to Billing Portal so plan swap goes through Stripe's
  // proration flow instead of double-charging via a new Checkout session.
  const handlePlanAction = async (targetPlan) => {
    if (targetPlan === plan) return;

    if (isPaidPlan && hasStripeCustomer) {
      await openPortal();
      return;
    }

    setCheckingOut(targetPlan);
    try {
      const { data, error } = await supabase.functions.invoke(
        'create-checkout-session',
        { body: { plan: targetPlan, period: billing } },
      );
      if (error) throw error;
      if (data?.url) {
        window.location.href = data.url;
      } else {
        throw new Error('Checkout URL not returned');
      }
    } catch (err) {
      console.error('Checkout failed:', err);
      toast.error('Não foi possível iniciar o pagamento. Tente novamente.');
      setCheckingOut(null);
    }
  };

  const renderPlanButton = (targetPlan) => {
    const isCurrent = plan === targetPlan;
    const loading = checkingOut === targetPlan;
    const accent =
      targetPlan === 'platinum'
        ? 'bg-[#047857] hover:bg-[#059669] text-white'
        : 'border-2 border-[#1B2A4A] text-[#1B2A4A] hover:bg-[#1B2A4A] hover:text-white bg-white';

    if (isCurrent) {
      return (
        <Button
          disabled
          className="w-full h-14 text-base font-bold bg-slate-200 text-slate-600 rounded-xl cursor-default"
        >
          <Check size={18} className="mr-2" />
          Plano atual
        </Button>
      );
    }

    const label = isPaidPlan
      ? `Trocar para ${targetPlan === 'platinum' ? 'Platinum' : 'Diamond'}`
      : `Escolher ${targetPlan === 'platinum' ? 'Platinum' : 'Diamond'}`;

    return (
      <Button
        onClick={() => handlePlanAction(targetPlan)}
        disabled={loading || opening || checkingOut}
        className={`w-full h-14 text-base font-bold rounded-xl ${accent}`}
        data-testid={`plan-${targetPlan}-btn`}
      >
        {loading ? (
          <>
            <Loader2 size={18} className="mr-2 animate-spin" />
            Redirecionando...
          </>
        ) : (
          label
        )}
      </Button>
    );
  };

  return (
    <Layout showMonthSelector={false}>
      <div className="space-y-6 animate-fade-in" data-testid="minha-conta-page">
        {/* Header */}
        <div className="flex items-center gap-3">
          <CreditCard size={28} className="text-[#1B2A4A]" />
          <h2 className="text-2xl font-bold text-[#1B2A4A]">Minha Conta</h2>
        </div>

        {/* User summary */}
        <Card className="border-2 border-slate-200 shadow-lg">
          <CardContent className="pt-5 pb-5">
            <p className="text-sm text-slate-500">Conectado como</p>
            <p className="text-lg font-semibold text-[#1B2A4A]">
              {user?.nome || 'Usuário'}
            </p>
            <p className="text-base text-slate-600">{user?.email}</p>
          </CardContent>
        </Card>

        {/* Plan card */}
        <Card className={`border-2 shadow-lg ${info.border} ${info.bg}`} data-testid="plan-card">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-3">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center bg-white shadow-sm ${info.color}`}>
                <PlanIcon size={26} />
              </div>
              <div>
                <p className="text-sm font-semibold text-slate-500">Seu plano atual</p>
                <p className={`text-2xl font-bold ${info.color}`}>{info.label}</p>
              </div>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-base text-slate-700 leading-relaxed">
              {info.description}
            </p>

            {/* Status + renewal */}
            {isPaidPlan && (statusMeta || periodEnd) && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {statusMeta && (
                  <div className="bg-white border-2 border-slate-200 rounded-xl p-3">
                    <p className="text-sm text-slate-500">Situação</p>
                    <span className={`inline-block mt-1 px-3 py-1 rounded-lg text-sm font-bold ${statusMeta.bg} ${statusMeta.color}`}>
                      {statusMeta.label}
                    </span>
                  </div>
                )}
                {periodEnd && (
                  <div className="bg-white border-2 border-slate-200 rounded-xl p-3">
                    <p className="text-sm text-slate-500 flex items-center gap-1">
                      <CalendarCheck size={14} />
                      Próxima renovação
                    </p>
                    <p className="text-base font-semibold text-[#1B2A4A] mt-1">
                      {formatBRDate(periodEnd) || '—'}
                    </p>
                  </div>
                )}
              </div>
            )}

            {isPaidPlan && hasStripeCustomer && (
              <Button
                onClick={openPortal}
                disabled={opening || Boolean(checkingOut)}
                className="w-full h-14 text-base font-semibold bg-[#1B2A4A] hover:bg-[#2C3E50] text-white rounded-xl"
                data-testid="manage-subscription-btn"
              >
                {opening ? (
                  <>
                    <Loader2 size={20} className="mr-2 animate-spin" />
                    Abrindo...
                  </>
                ) : (
                  <>
                    <Settings2 size={20} className="mr-2" />
                    Gerenciar assinatura
                  </>
                )}
              </Button>
            )}

            {isPaidPlan && (
              <p className="text-sm text-slate-600 flex items-start gap-2">
                <Info size={16} className="shrink-0 mt-0.5" />
                <span>
                  Em "Gerenciar assinatura" você pode cancelar, atualizar o cartão
                  e ver as faturas.
                </span>
              </p>
            )}
          </CardContent>
        </Card>

        {/* Pricing — only hide when user is already on Diamond (nothing to upgrade to). */}
        {plan !== 'diamond' && (
          <Card className="border-2 border-slate-200 shadow-lg">
            <CardHeader className="pb-2">
              <CardTitle className="text-xl font-bold text-[#1B2A4A]">
                {isPaidPlan ? 'Trocar de plano' : 'Escolha um plano'}
              </CardTitle>
              <p className="text-base text-slate-600 mt-1">
                {isPaidPlan
                  ? 'Você será levado ao gerenciamento da assinatura para trocar o plano com cobrança proporcional.'
                  : 'Comece agora e pague só pelo que usar.'}
              </p>
            </CardHeader>
            <CardContent className="space-y-5">
              {/* Billing period toggle */}
              <div className="flex justify-center">
                <div className="inline-flex bg-slate-100 border-2 border-slate-200 rounded-xl p-1">
                  <button
                    type="button"
                    onClick={() => setBilling('monthly')}
                    className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${
                      billing === 'monthly'
                        ? 'bg-[#1B2A4A] text-white'
                        : 'text-slate-600 hover:text-[#1B2A4A]'
                    }`}
                    data-testid="billing-monthly-btn"
                  >
                    Mensal
                  </button>
                  <button
                    type="button"
                    onClick={() => setBilling('annual')}
                    className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors flex items-center gap-2 ${
                      billing === 'annual'
                        ? 'bg-[#1B2A4A] text-white'
                        : 'text-slate-600 hover:text-[#1B2A4A]'
                    }`}
                    data-testid="billing-annual-btn"
                  >
                    Anual
                    <span
                      className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                        billing === 'annual'
                          ? 'bg-[#047857] text-white'
                          : 'bg-green-100 text-[#047857]'
                      }`}
                    >
                      2 meses grátis
                    </span>
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                {/* Platinum */}
                <div className="relative border-4 border-[#047857] rounded-2xl p-5 flex flex-col bg-white">
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#047857] text-white text-xs font-bold px-3 py-1 rounded-full flex items-center gap-1 shadow-md">
                    <Star size={12} className="fill-white" />
                    Mais Popular
                  </div>
                  <h3 className="text-xl font-bold text-[#047857] mt-2">Platinum</h3>
                  <div className="mt-2 mb-3">
                    <span className="text-3xl font-extrabold text-[#1B2A4A]">
                      {PRICES.platinum[billing].amount}
                    </span>
                    <span className="text-sm text-slate-500 ml-2">
                      {PRICES.platinum[billing].suffix}
                    </span>
                  </div>
                  <FeatureList features={PLATINUM_FEATURES} />
                  {renderPlanButton('platinum')}
                </div>

                {/* Diamond */}
                <div className="border-2 border-slate-200 rounded-2xl p-5 flex flex-col bg-white">
                  <h3 className="text-xl font-bold text-[#1B2A4A]">Diamond</h3>
                  <div className="mt-2 mb-3">
                    <span className="text-3xl font-extrabold text-[#1B2A4A]">
                      {PRICES.diamond[billing].amount}
                    </span>
                    <span className="text-sm text-slate-500 ml-2">
                      {PRICES.diamond[billing].suffix}
                    </span>
                  </div>
                  <FeatureList features={DIAMOND_FEATURES} />
                  {renderPlanButton('diamond')}
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
}

export default MinhaContaPage;
